import React, { ReactElement, useState } from "react";

import { css, cx } from "@emotion/css";
import {
  useFloating,
  autoUpdate,
  offset,
  Placement,
  flip,
  shift,
  FloatingPortal,
  useTransitionStyles,
  useInteractions,
  useClick,
  useDismiss,
} from "@floating-ui/react";

import { useStyles } from "@/hooks/useStyles.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import type { ButtonProps } from "@/uiKit/atoms/Button.tsx";

export type TooltipV2Props = {
  /** Id of the popup (the trigger is the cloned child, which carries its own data-action). */
  popupId?: string;
  placement: Placement;
  content: React.ReactElement<ButtonProps>[];
  floaterClassName?: string;
  children: ReactElement;
};

export const TooltipMenuV2: React.FC<TooltipV2Props> = ({
  popupId,
  placement,
  children,
  content,
  floaterClassName,
}) => {
  const styles = useStyles(makeStyles);
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating<Element>({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(), flip(), shift()],
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  const { isMounted, styles: animationStyle } = useTransitionStyles(context, {
    duration: 150,
    initial: { transform: "scale(0.2)" },
    common: ({ placement }) => ({
      transformOrigin: {
        top: "bottom",
        bottom: "top",
        left: "right",
        right: "left",
        "top-start": "bottom left",
        "bottom-start": "top left",
        "left-start": "top right",
        "right-start": "top left",
        "top-end": "bottom right",
        "bottom-end": "top right",
        "left-end": "bottom right",
        "right-end": "bottom left",
      }[placement],
    }),
  });

  const newChild = React.cloneElement(children, {
    ...(children.props || {}),
    ref: refs.setReference,
    ...getReferenceProps(),
  } as {});

  return (
    <>
      {newChild}
      {isMounted ? (
        <FloatingPortal>
          <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()} className={styles.floatManager}>
            <div id={popupId} style={animationStyle} className={cx(styles.floater, floaterClassName)}>
              {content}
            </div>
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  floatManager: css({
    zIndex: theme.zIndex.tooltip,
  }),
  floater: css({
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(4),
    boxShadow: "1px 1px 2px 0.5px lightgrey",
    overflow: "clip",
    display: "flex",
    flexDirection: "column",
    color: "red",
    "> *": {
      justifyContent: "flex-start",
    },
  }),
});
